import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { CiRunList, FleetUpdating } from '@one-deployment/api'
import { get } from '@/lib/api'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { useCiRun } from '@/features/ci/Ci'
import { PipelineGraph } from '@/features/ci/Pipeline'

/** The platform's deploy jobs running now; polled while a session is open. */
export function useFleetUpdating() {
  return useQuery({ queryKey: ['fleet', 'updating'], queryFn: () => get<FleetUpdating>('/api/fleet/updating'), refetchInterval: 15_000, staleTime: 10_000 })
}

/**
 * Above a session: while the servers' agents are being updated a session
 * drops for a minute, so say to wait rather than show "Cannot reach the
 * server" as if something broke. Otherwise the plain unreachable line.
 */
export function FleetNotice({ unreachable }: { unreachable: boolean }) {
  const jobs = useFleetUpdating().data?.jobs ?? []
  if (jobs.length) {
    return (
      <div role="status" className="border-b border-warn/30 bg-warn/10 px-3 py-1.5 text-xs">
        Servers are being updated. {unreachable ? 'Wait until it finishes; this session reconnects by itself.' : 'This session may drop for a minute and reconnect by itself.'}{' '}
        <Link to="/p/$project/ci/$runId" params={{ project: 'one-deployment', runId: jobs[0]!.runId }} className="underline">See the deployment</Link>
      </div>
    )
  }
  if (!unreachable) return null
  return <div role="alert" className="border-b border-err/30 bg-err/10 px-3 py-1.5 text-xs">Cannot reach the server. Still retrying.</div>
}

/**
 * The "1" of the logo. While One Deployment's own pipeline runs it turns
 * turquoise and pulses, and opens the running deploy: a deploy restarts the
 * servers' agents, so it should never come as a surprise. It used to be a
 * "Deploying" pill beside Push to prod, which read as the project's own deploy.
 */
export function DeployOne() {
  const runs = useQuery({ queryKey: ['fleet', 'pipeline'], queryFn: () => get<CiRunList>('/api/projects/one-deployment/ci/runs?limit=5'), refetchInterval: 15_000, retry: false })
  const run = runs.data?.runs.find((r) => r.status === 'running' || r.status === 'queued')
  if (!run) return <Link to="/" className="leading-none font-bold text-white">1</Link>
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button aria-label="One Deployment is deploying — see it" title="One Deployment is deploying — see it"
          className="rounded-sm leading-none font-bold text-info motion-safe:animate-pulse">1</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(90vw,64rem)] space-y-2 p-3">
        <DeployPreview runId={run.id} />
      </PopoverContent>
    </Popover>
  )
}

/** The running deploy's pipeline, live, with a way into the full run. */
function DeployPreview({ runId }: { runId: string }) {
  const q = useCiRun('one-deployment', runId)
  if (!q.data) return <p className="text-xs text-fg-muted">Loading…</p>
  const { run, jobs } = q.data
  return (
    <>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium text-fg">{run.branch}</span>
        <span className="font-mono text-fg-faint">{run.sha.slice(0, 7)}</span>
        <Link to="/p/$project/ci/$runId" params={{ project: 'one-deployment', runId }} className="ml-auto text-accent hover:underline">Open run</Link>
      </div>
      <PipelineGraph nodes={jobs} />
    </>
  )
}
